import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "frontend-components/lib/utils";

/**
 * Single source of truth for every badge color/shade.
 *
 * Each entry holds the full class string for both appearances and covers every
 * color in the Compendia palette at every shade the design tokens define.
 * Classes are written out literally (never built as `bg-chameleon-${shade}`) so
 * Tailwind's scanner can detect them — dynamically composed class names are not
 * emitted.
 *
 * Text-color rules:
 *  - Vivid/dark fills get white text; light fills/tints get `compendia-gray-80`.
 *  - Outlines color the border with the variant color and keep the label dark
 *    (the strong brand colors reuse their own hue for the label).
 */
const badgeColors = {
    // Green
    green: {
        fill: "bg-chameleon-green text-white",
        outline: "border-chameleon-green text-chameleon-green",
    },
    "green-60": {
        fill: "bg-chameleon-green-60 text-compendia-gray-80",
        outline: "border-chameleon-green-60 text-compendia-gray-80",
    },
    "green-40": {
        fill: "bg-chameleon-green-40 text-compendia-gray-80",
        outline: "border-chameleon-green-40 text-compendia-gray-80",
    },
    "green-20": {
        fill: "bg-chameleon-green-20 text-compendia-gray-80",
        outline: "border-chameleon-green-20 text-compendia-gray-80",
    },
    "green-10": {
        fill: "bg-chameleon-green-10 text-compendia-gray-80",
        outline: "border-chameleon-green-10 text-compendia-gray-80",
    },

    // Pink
    pink: {
        fill: "bg-chameleon-pink text-white",
        outline: "border-chameleon-pink text-chameleon-pink",
    },
    "pink-60": {
        fill: "bg-chameleon-pink-60 text-compendia-gray-80",
        outline: "border-chameleon-pink-60 text-compendia-gray-80",
    },
    "pink-40": {
        fill: "bg-chameleon-pink-40 text-compendia-gray-80",
        outline: "border-chameleon-pink-40 text-compendia-gray-80",
    },
    "pink-20": {
        fill: "bg-chameleon-pink-20 text-compendia-gray-80",
        outline: "border-chameleon-pink-20 text-compendia-gray-80",
    },
    "pink-10": {
        fill: "bg-chameleon-pink-10 text-compendia-gray-80",
        outline: "border-chameleon-pink-10 text-compendia-gray-80",
    },

    // Yellow
    yellow: {
        fill: "bg-chameleon-yellow text-compendia-gray-80",
        outline: "border-chameleon-yellow text-compendia-gray-80",
    },
    "yellow-60": {
        fill: "bg-chameleon-yellow-60 text-compendia-gray-80",
        outline: "border-chameleon-yellow-60 text-compendia-gray-80",
    },
    "yellow-40": {
        fill: "bg-chameleon-yellow-40 text-compendia-gray-80",
        outline: "border-chameleon-yellow-40 text-compendia-gray-80",
    },
    "yellow-20": {
        fill: "bg-chameleon-yellow-20 text-compendia-gray-80",
        outline: "border-chameleon-yellow-20 text-compendia-gray-80",
    },
    "yellow-10": {
        fill: "bg-chameleon-yellow-10 text-compendia-gray-80",
        outline: "border-chameleon-yellow-10 text-compendia-gray-80",
    },

    // Teal (surface-teal scale: dark → light)
    teal: {
        fill: "bg-surface-teal-dark text-white",
        outline: "border-surface-teal-dark text-surface-teal-dark",
    },
    "teal-light": {
        fill: "bg-surface-teal-light text-compendia-gray-80",
        outline: "border-surface-teal-light text-compendia-gray-80",
    },

    // Blue (info)
    blue: {
        fill: "bg-alert-info text-white",
        outline: "border-alert-info text-alert-info",
    },

    // Bone
    bone: {
        fill: "bg-surface-bone text-compendia-gray-80",
        outline: "border-surface-bone text-compendia-gray-80",
    },

    // Greige
    "greige-dark": {
        fill: "bg-compendia-greige-dark text-compendia-gray-80",
        outline: "border-compendia-greige-dark text-compendia-gray-80",
    },
    greige: {
        fill: "bg-compendia-greige text-compendia-gray-80",
        outline: "border-compendia-greige text-compendia-gray-80",
    },
    "greige-60": {
        fill: "bg-compendia-greige-60 text-compendia-gray-80",
        outline: "border-compendia-greige-60 text-compendia-gray-80",
    },
    "greige-40": {
        fill: "bg-compendia-greige-40 text-compendia-gray-80",
        outline: "border-compendia-greige-40 text-compendia-gray-80",
    },
    "greige-20": {
        fill: "bg-compendia-greige-20 text-compendia-gray-80",
        outline: "border-compendia-greige-20 text-compendia-gray-80",
    },
    "greige-10": {
        fill: "bg-compendia-greige-10 text-compendia-gray-80",
        outline: "border-compendia-greige-10 text-compendia-gray-80",
    },

    // Neutral gray (gray = gray-80, the darkest step)
    gray: {
        fill: "bg-compendia-gray-80 text-white",
        outline: "border-compendia-gray-80 text-compendia-gray-80",
    },
    "gray-60": {
        fill: "bg-compendia-gray-60 text-white",
        outline: "border-compendia-gray-60 text-compendia-gray-80",
    },
    "gray-40": {
        fill: "bg-compendia-gray-40 text-compendia-gray-80",
        outline: "border-compendia-gray-40 text-compendia-gray-80",
    },
    "gray-20": {
        fill: "bg-compendia-gray-20 text-compendia-gray-80",
        outline: "border-compendia-gray-20 text-compendia-gray-80",
    },

    // Black
    dark: {
        fill: "bg-compendia-black text-white",
        outline: "border-compendia-black text-compendia-black",
    },
} as const;

type BadgeVariant = keyof typeof badgeColors;

const BADGE_VARIANTS = Object.keys(badgeColors) as BadgeVariant[];

const badgeVariants = cva(
    "group/badge rounded-pill focus-visible:ring-ring/50 inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden border border-transparent px-[13px] py-1 text-[0.7rem] font-bold whitespace-nowrap transition-colors focus-visible:ring-[3px] [&>svg]:pointer-events-none [&>svg]:size-3!",
    {
        variants: {
            appearance: {
                fill: "",
                outline: "bg-transparent",
            },
        },
        defaultVariants: {
            appearance: "fill",
        },
    },
);

function Badge({
    className,
    variant = "greige",
    appearance = "fill",
    render,
    ...props
}: useRender.ComponentProps<"span"> &
    Omit<VariantProps<typeof badgeVariants>, "appearance"> & {
        variant?: BadgeVariant;
        appearance?: "fill" | "outline";
    }) {
    return useRender({
        defaultTagName: "span",
        props: mergeProps<"span">(
            {
                className: cn(
                    badgeVariants({ appearance }),
                    badgeColors[variant][appearance],
                    className,
                ),
            },
            props,
        ),
        render,
        state: {
            slot: "badge",
            variant,
            appearance,
        },
    });
}

export { Badge, badgeVariants, badgeColors, BADGE_VARIANTS };
export type { BadgeVariant };
